import { LatticeRuntime, InMemoryPersistenceProvider } from '@noven-prime/lattice-core';
import { defaultPortalSettings } from '@denise-augusta/domain';
import {
  latticeConfig,
  siteContentManifest,
  mediaLibraryManifest,
  portalSettingsManifest,
  aiAssistManifest,
  formSubmissionsManifest,
  registerSiteContentStore,
  registerMediaStore,
  registerPortalSettingsStore,
  registerSubmissionsStore,
  registerLlmConfigProvider,
} from '@denise-augusta/capabilities';
import {
  createAzureSiteContentStore,
  createAzureMediaStore,
  createAzurePortalSettingsStore,
  createAzureSubmissionsStore,
} from '@/lib/blob-stores';
import { environment } from '@/config/environment';

/**
 * Browser-hosted Lattice runtime.
 *
 * This is a static web app with no API tier, so the runtime lives in the
 * browser: capability actions execute in-process and their stores talk
 * directly to Azure Blob Storage (Azurite in local dev). Pattern mirrors
 * foyale's server runtime bootstrap.
 */
export const runtime = new LatticeRuntime({
  config: latticeConfig,
  persistence: new InMemoryPersistenceProvider(),
  devMode: environment.name !== 'live',
});

let initialized: Promise<void> | null = null;

export function initializeLattice(): Promise<void> {
  if (!initialized) {
    initialized = (async () => {
      await runtime.initialize();

      // Wire the environment's storage handlers before registering the
      // capabilities that depend on them.
      registerSiteContentStore(createAzureSiteContentStore());
      registerMediaStore(createAzureMediaStore());
      registerSubmissionsStore(createAzureSubmissionsStore());
      const settingsStore = createAzurePortalSettingsStore();
      registerPortalSettingsStore(settingsStore);
      // ai-assist reads its LLM config through portal settings — wired here
      // rather than imported across capabilities, the lattice way.
      registerLlmConfigProvider(async () => {
        const settings = await settingsStore.load();
        return (settings ?? defaultPortalSettings).llm;
      });

      runtime.registerCapability(siteContentManifest);
      runtime.registerCapability(mediaLibraryManifest);
      runtime.registerCapability(portalSettingsManifest);
      runtime.registerCapability(aiAssistManifest);
      runtime.registerCapability(formSubmissionsManifest);
    })();
  }
  return initialized;
}

export interface PortalUser {
  id: string;
  scope: string;
  attributes: Record<string, unknown>;
}

/**
 * Execute a lattice action and unwrap the response, throwing on failure.
 */
export async function executeAction<TResult>(
  actionId: string,
  params: Record<string, unknown>,
  user: PortalUser
): Promise<TResult> {
  await initializeLattice();
  const response = await runtime.executeAction({ actionId, params, user });
  if (!response.success) {
    throw new Error(response.error?.message ?? `Action ${actionId} failed`);
  }
  return response.result as TResult;
}
